import { FieldSwitch, FlexBox, Slider } from '@dixa/lib-shared-habla';
import { useCallback, useState } from 'react';
import { useUpdateEffect } from 'react-use';

import {
  convertMinutesToAbsoluteTime,
  convertToHumanRelativeTime,
  convertToTotalMinutesIntoDay,
} from 'components/powerFlowShared/helpers/timeHelpers';

import {
  OpeningHoursNodeIntervalsT,
  OpeningHoursNodeScheduleT,
} from '../../nodeTypes';

import styles from './day.module.scss';

interface Props {
  day: OpeningHoursNodeScheduleT;
  index: number;
  onDayChange: (
    weekDay: number,
    intervals: OpeningHoursNodeIntervalsT[],
  ) => void;
}

interface Time {
  absoluteTimeClose: string;
  absoluteTimeOpen: string;
}

const DEFAULT_OPEN_MINS = 540;
const DEFAULT_OPEN_ABS = convertMinutesToAbsoluteTime(DEFAULT_OPEN_MINS);
const DEFAULT_CLOSE_MINS = 1050;
const DEFAULT_CLOSE_ABS = convertMinutesToAbsoluteTime(DEFAULT_CLOSE_MINS);

export function Day({ day, index, onDayChange }: Props): JSX.Element {
  const { intervals } = day;
  const dayInterval = intervals[0] as OpeningHoursNodeIntervalsT | undefined;

  const absoluteTimeOpen = dayInterval?.open || DEFAULT_OPEN_ABS;
  const absoluteTimeClose = dayInterval?.close || DEFAULT_CLOSE_ABS;

  const [isOpen, setIsOpen] = useState<boolean>(!!dayInterval);
  const [time, setTime] = useState<Time>({
    absoluteTimeOpen,
    absoluteTimeClose,
  });

  const onIsOpenChange = useCallback(() => {
    setIsOpen((isOpen) => !isOpen);
  }, [setIsOpen]);

  const onSliderChange = useCallback(
    ([open, close]) => {
      const absoluteTimeOpen = convertMinutesToAbsoluteTime(open);
      const absoluteTimeClose = convertMinutesToAbsoluteTime(close);
      setTime({ absoluteTimeOpen, absoluteTimeClose });
    },
    [setTime],
  );

  useUpdateEffect(() => {
    const { absoluteTimeOpen: open, absoluteTimeClose: close } = time;
    const interval = isOpen ? [{ open, close }] : [];
    onDayChange(index, interval);
  }, [index, isOpen, onDayChange, time]);

  const weekDayName = new Date(day.date).toLocaleString(['en-GB'], {
    weekday: 'long',
    timeZone: 'UTC',
  });

  const sliderValues: [number, number] = [
    time.absoluteTimeOpen
      ? convertToTotalMinutesIntoDay(time.absoluteTimeOpen)
      : DEFAULT_OPEN_MINS,
    time.absoluteTimeClose
      ? convertToTotalMinutesIntoDay(time.absoluteTimeClose)
      : DEFAULT_CLOSE_MINS,
  ];

  return (
    <FlexBox flexDirection="column" className={styles.root}>
      <FlexBox
        justifyContent="space-between"
        alignItems="center"
        className={styles.weekDayWrapper}
      >
        <div className={styles.weekDayName}>{weekDayName}</div>
        <FieldSwitch
          className={styles.fieldSwitch}
          input={{
            checked: isOpen,
            name: 'active',
            value: 'active',
            onChange: onIsOpenChange,
          }}
        />
      </FlexBox>
      <FlexBox
        justifyContent="space-between"
        alignItems="center"
        className={styles.timeWrapper}
      >
        <div className={styles.timeValue}>
          {isOpen
            ? convertToHumanRelativeTime(time.absoluteTimeOpen)
            : 'Closed'}
        </div>
        {isOpen && (
          <div className={styles.sliderWrapper}>
            <Slider
              min={0}
              max={1439}
              step={15}
              value={sliderValues}
              onChange={onSliderChange}
              hideAnnotations
            />
          </div>
        )}
        {isOpen && time.absoluteTimeClose && (
          <div className={styles.timeValue}>
            {convertToHumanRelativeTime(time.absoluteTimeClose)}
          </div>
        )}
      </FlexBox>
    </FlexBox>
  );
}
